if (!customElements.get('shoev-video-grid')) {
  customElements.define('shoev-video-grid', class extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      this._controller = new AbortController();
      this._items = Array.from(this.querySelectorAll('[data-video-item]'));
      this._mobile = window.matchMedia('(max-width: 749px)');
      this._rail = this.querySelector('[data-video-rail]');
      this._previous = this.querySelector('[data-video-previous]');
      this._next = this.querySelector('[data-video-next]');

      this._items.forEach((item) => this.setupItem(item));
      this.setupRailControls();
      this._mediaChange = () => this.setupVisibilityObserver();
      if (this._mobile.addEventListener) this._mobile.addEventListener('change', this._mediaChange, { signal: this._controller.signal });
      else this._mobile.addListener(this._mediaChange);
      this.setupVisibilityObserver();
    }

    disconnectedCallback() {
      if (this._observer) this._observer.disconnect();
      if (this._controller) this._controller.abort();
      if (this._mobile && !this._mobile.addEventListener && this._mediaChange) this._mobile.removeListener(this._mediaChange);
      this._initialized = false;
    }

    pauseOthers(current) {
      this._items.forEach((item) => {
        if (item === current) return;
        const video = item.querySelector('video');
        if (video && !video.paused) video.pause();
      });
    }

    setupRailControls() {
      if (!this._rail || !this._previous || !this._next) return;
      const signal = this._controller.signal;
      const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const scrollByCard = (direction) => {
        const firstCard = this._items[0];
        const amount = firstCard ? Math.round(firstCard.getBoundingClientRect().width * 1.15) : Math.round(this._rail.clientWidth * .8);
        this._rail.scrollBy({ left: direction * amount, behavior: reducedMotion() ? 'auto' : 'smooth' });
      };
      this._previous.addEventListener('click', () => scrollByCard(-1), { signal });
      this._next.addEventListener('click', () => scrollByCard(1), { signal });
      const sync = () => {
        const max = Math.max(0, this._rail.scrollWidth - this._rail.clientWidth);
        this._previous.disabled = this._rail.scrollLeft <= 2;
        this._next.disabled = this._rail.scrollLeft >= max - 2;
      };
      this._rail.addEventListener('scroll', sync, { signal, passive: true });
      window.addEventListener('resize', sync, { signal });
      sync();
    }

    setupItem(item) {
      const video = item.querySelector('video');
      const toggle = item.querySelector('[data-video-toggle]');
      const mute = item.querySelector('[data-video-mute]');
      if (!video || !toggle) {
        if (toggle) toggle.disabled = true;
        return;
      }

      const signal = this._controller.signal;
      const playLabel = toggle.getAttribute('aria-label');
      const syncPlayback = () => {
        const playing = !video.paused && !video.ended;
        item.classList.toggle('is-playing', playing);
        toggle.setAttribute('aria-pressed', playing ? 'true' : 'false');
        toggle.setAttribute('aria-label', playing ? 'Pause video' : playLabel);
        if (!mute) return;
        mute.disabled = !playing;
        mute.setAttribute('aria-hidden', playing ? 'false' : 'true');
        mute.setAttribute('tabindex', playing ? '0' : '-1');
      };
      const syncMute = () => {
        if (!mute) return;
        const muted = video.muted || video.volume === 0;
        mute.setAttribute('aria-pressed', muted ? 'true' : 'false');
        mute.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video');
      };

      toggle.addEventListener('click', () => {
        if (!video.paused && !video.ended) {
          video.pause();
          return;
        }
        this.pauseOthers(item);
        const prefersMuted = window.__shoevVideoMutedPreference === true;
        video.muted = prefersMuted;
        if (!prefersMuted && video.volume === 0) video.volume = 1;
        syncMute();
        const promise = video.play();
        if (promise && promise.catch) {
          promise.catch(() => {
            video.muted = true;
            syncMute();
            const fallback = video.play();
            if (fallback && fallback.catch) fallback.catch(syncPlayback);
          });
        }
      }, { signal });

      if (mute) {
        mute.addEventListener('click', () => {
          const muted = video.muted || video.volume === 0;
          if (muted && video.volume === 0) video.volume = 1;
          video.muted = !muted;
          window.__shoevVideoMutedPreference = video.muted;
          syncMute();
        }, { signal });
      }

      video.addEventListener('play', syncPlayback, { signal });
      video.addEventListener('pause', syncPlayback, { signal });
      video.addEventListener('ended', syncPlayback, { signal });
      video.addEventListener('volumechange', syncMute, { signal });
      syncPlayback();
      syncMute();
    }

    setupVisibilityObserver() {
      if (this._observer) this._observer.disconnect();
      this._observer = null;
      if (!this._mobile.matches || !('IntersectionObserver' in window)) return;

      this._observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.intersectionRatio >= 0.62) return;
          const video = entry.target.querySelector('video');
          if (video && !video.paused) video.pause();
        });
      }, { root: this.querySelector('.shoev-vgrid__grid'), threshold: [0, 0.62, 1] });

      this._items.forEach((item) => this._observer.observe(item));
    }
  });
}
